import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { FormatService } from '@/app/common/format/format.service';
import { TtoCampaignVideoDto } from '@/app/common/general-types/tiktok-one-reporting.dto';

interface KeyMetric {
  id: string;
  label: string;
  value: string;
}

interface TrafficSource {
  label: string;
  percentage: string;
  widthPct: number;
}

const SOURCE_LABELS: Record<string, string> = {
  for_you: 'For You',
  others: 'Other',
  personal_profile: 'Personal profile',
  search: 'Search',
  following: 'Following',
  hashtag: 'Hashtag',
  sound: 'Sound',
  direct_messages: 'Direct messages',
  unknown: 'Unknown',
};

function formatTotalPlayTimeSeconds(totalSeconds: number): string {
  if (totalSeconds <= 0) {
    return '0s';
  }
  const whole = Math.floor(totalSeconds);
  const s = whole % 60;
  const m = Math.floor((whole % 3600) / 60);
  const h = Math.floor(whole / 3600);
  const pad = (n: number) => n.toString().padStart(2, '0');
  if (h > 0) {
    return `${h}h:${pad(m)}m:${pad(s)}s`;
  }
  if (m > 0) {
    return `${m}m:${pad(s)}s`;
  }
  return `${s}s`;
}

function formatAverageViewSeconds(seconds: number): string {
  const roundedTenth = Math.round(seconds * 10) / 10;
  if (Number.isInteger(roundedTenth)) {
    return `${roundedTenth}s`;
  }
  return `${roundedTenth.toFixed(1)}s`;
}

function formatCompletionRatePercent(rate: number | null | undefined): string {
  if (rate == null || Number.isNaN(Number(rate))) {
    return '—';
  }
  const n = Number(rate);
  return `${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
}

@Component({
  selector: 'vat-analysis-overview',
  imports: [],
  templateUrl: './analysis-overview.html',
  styleUrl: './analysis-overview.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalysisOverview {
  readonly video = input.required<TtoCampaignVideoDto>();

  readonly selectedMetric = signal<string>('video_views');

  private readonly formatService = inject(FormatService);

  readonly trafficSources = computed<TrafficSource[]>(() => {
    const raw = this.video().video_views_by_source as
      Array<{ source: string; videoViews: number }> | null;
    if (!raw?.length) return [];
    const total = raw.reduce((sum, s) => sum + s.videoViews, 0);
    if (total === 0) return [];
    const sorted = [...raw].sort((a, b) => b.videoViews - a.videoViews);
    return sorted.map(s => {
      const pct = (s.videoViews / total) * 100;
      return {
        label: SOURCE_LABELS[s.source] ?? s.source,
        percentage: `${(Math.round(pct * 10) / 10).toFixed(1)}%`,
        widthPct: pct,
      };
    });
  });

  readonly keyMetrics = computed<KeyMetric[]>(() => {
    const v = this.video();
    return [
      {
        id: 'video_views',
        label: 'Video views',
        value: this.formatService.convertIntToString(v.video_views),
      },
      {
        id: 'total_play_time',
        label: 'Total play time',
        value: formatTotalPlayTimeSeconds(v.total_play_time),
      },
      {
        id: 'avg_watch_time',
        label: 'Average watch time',
        value: formatAverageViewSeconds(v.average_view_time),
      },
      {
        id: 'watched_full',
        label: 'Watched full video',
        value: formatCompletionRatePercent(v.video_completion_rate * 100),
      },
    ];
  });
}
